import { FC, useRef } from 'react';
import { Box, Flex, ListItem, UnorderedList } from '@chakra-ui/react';
import { PaymentCardType } from '@shinetools/pricing-plan-library';

import Card from 'components/_core/Card';
import SunshineIcon from 'components/_core/SunshineIcon';
import SunshineTag from 'components/_core/SunshineTag';
import Text from 'components/_core/Text';
import DecorativeImage from 'components/DecorativeImage';

import basicCard from '../../../../assets/rebranded/basicCard.png';
import doubleCard from '../../../../assets/rebranded/doubleCard.png';
import businessWorldCard from '../../../../assets/rebranded/plusCard.png';
import tripleCard from '../../../../assets/rebranded/tripleCard.png';
import PlanOption from '../../../../components/PlanOption';
import { OnboardingPlanDisplay } from '../../../../types';
import featuresBuilder from './featuresBuilder';
import locales from './locales';

interface PlanCardProps {
  plan: OnboardingPlanDisplay;
  onChoose: (plan: OnboardingPlanDisplay) => void;
}

const getIcon = (plan: OnboardingPlanDisplay) => {
  if (plan.paymentCard.type === PaymentCardType.BasicDebitCard) {
    return basicCard;
  }
  if (plan.limits.physicalPaymentCard.value === 2) {
    return doubleCard;
  }
  if (plan.limits.physicalPaymentCard.value > 2) {
    return tripleCard;
  }
  return businessWorldCard;
};

const PlanCard: FC<PlanCardProps> = ({ onChoose, plan }) => {
  const features = featuresBuilder[plan.providerPlanId](plan);
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <Card
      background="white"
      border="none"
      cursor="pointer"
      paddingX="space-16"
      position="relative"
      width="100%"
    >
      {plan.providerPlanId === 'plus' ? (
        <Text
          fontWeight="weight-500"
          left="50%"
          position="absolute"
          size="sm"
          top="space-20"
          transform="translateX(-50%)"
          variant="primary"
          whiteSpace="nowrap"
          zIndex={2}
        >
          <SunshineIcon marginRight="space-4" name="star" size="icon-14" />
          {locales.recommended}
        </Text>
      ) : null}
      <Flex
        background="grey.200"
        height="150px"
        justifyContent="center"
        marginBottom="space-16"
        marginTop="-space-16"
        marginX="-space-16"
        overflow="hidden"
        paddingTop="space-16"
      >
        <Box marginTop="space-20">
          <DecorativeImage heightPx={155} src={getIcon(plan)} widthPx={200} />
        </Box>
      </Flex>
      <Box marginBottom="space-40" ref={containerRef}>
        <PlanOption
          isButtonLoading={false}
          isMobile={true}
          onChoose={onChoose}
          plan={plan}
        />
      </Box>
      {features.map((feature, i) => (
        <Box key={feature.key}>
          <Text
            key={feature.key}
            marginBottom="space-24"
            marginTop={i > 0 ? 'space-24' : 'space-0'}
            size="sm"
            variant="light"
          >
            {locales.features[feature.key]}
          </Text>
          <UnorderedList listStyleType="none" margin={0} padding={0}>
            {feature.rows.map((item) => (
              <ListItem
                alignItems="center"
                display="flex"
                justifyContent="space-between"
                key={item.label}
                marginBottom="space-16"
              >
                <Flex alignItems="center">
                  <SunshineIcon
                    color={item.isActive ? 'text-primary' : 'text-discreet'}
                    marginRight="space-16"
                    name={item.isActive ? 'tick' : 'cross'}
                    size="icon-16"
                  />
                  <Text variant={item.isActive ? 'primary' : 'light'}>
                    {item.label}
                  </Text>
                </Flex>
                {item.isNew ? (
                  <SunshineTag colorScheme="green" size="sm">
                    {locales.new.toUpperCase()}
                  </SunshineTag>
                ) : null}
              </ListItem>
            ))}
          </UnorderedList>
        </Box>
      ))}
    </Card>
  );
};

export default PlanCard;
